import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Plus, Edit, DollarSign, Clock, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DENTAL_CATEGORIES = [
  "Exam", "Radiology", "Imaging", "Hygiene", "Preventive", "Periodontal",
  "Restorative", "Endodontics", "Extraction", "OralSurgery", "Prosthodontics",
  "Dentures", "Implants", "Orthodontics", "Cosmetic", "Appliances", "Sedation",
  "Emergency", "Admin", "Review", "Pediatric"
];

const DentalServices = () => {
  const { token, isOwner } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    service_name: '',
    category: '',
    duration_minutes: 30,
    default_price_jmd: 0,
    cdt_code: '',
    notes: '',
    is_active: true
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dental/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load dental services');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axios.put(`${API_URL}/api/dental/services/${editingService.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service updated');
      } else {
        await axios.post(`${API_URL}/api/dental/services`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service created');
      }
      setShowModal(false);
      setEditingService(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    }
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({
      service_name: service.service_name,
      category: service.category,
      duration_minutes: service.duration_minutes,
      default_price_jmd: service.default_price_jmd,
      cdt_code: service.cdt_code || '',
      notes: service.notes || '',
      is_active: service.is_active
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingService(null);
    setFormData({
      service_name: '',
      category: '',
      duration_minutes: 30,
      default_price_jmd: 0,
      cdt_code: '',
      notes: '',
      is_active: true
    });
    setShowModal(true);
  };

  const filteredServices = services.filter(svc => {
    const matchesSearch = svc.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          svc.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || svc.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const groupedServices = filteredServices.reduce((acc, svc) => {
    if (!acc[svc.category]) acc[svc.category] = [];
    acc[svc.category].push(svc);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Dental Services Catalog</h1>
            <p className="text-slate-500">{services.length} services available</p>
          </div>
          {isOwner && (
            <Button onClick={openCreateModal} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Service
            </Button>
          )}
        </div>

        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {DENTAL_CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Services Grid by Category */}
        {Object.entries(groupedServices).map(([category, categoryServices]) => (
          <div key={category} className="mb-8">
            <h2 className="text-lg font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">{category}</span>
              <span className="text-slate-400 text-sm">({categoryServices.length})</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryServices.map(service => (
                <div 
                  key={service.id} 
                  className="card p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => isOwner && openEditModal(service)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-slate-800">{service.service_name}</h3>
                    {isOwner && (
                      <Edit className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      J${service.default_price_jmd.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {service.duration_minutes} min
                    </span>
                  </div>
                  {service.notes && (
                    <p className="text-xs text-slate-400 mt-2">{service.notes}</p>
                  )}
                  {service.cdt_code && (
                    <span className="inline-block mt-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      CDT: {service.cdt_code}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Service Name</Label>
                <Input
                  value={formData.service_name}
                  onChange={(e) => setFormData({...formData, service_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {DENTAL_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Price (JMD)</Label>
                  <Input
                    type="number"
                    value={formData.default_price_jmd}
                    onChange={(e) => setFormData({...formData, default_price_jmd: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div>
                <Label>CDT Code (optional)</Label>
                <Input
                  value={formData.cdt_code}
                  onChange={(e) => setFormData({...formData, cdt_code: e.target.value})}
                  placeholder="e.g., D0120"
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  {editingService ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DentalServices;
